import type { ReactNode } from "react";

// Line icons for the chat screen, 20×20, drawn in the current text color.

type P = { size?: number };

function Icon({ size = 18, children }: P & { children: ReactNode }) {
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}

export const IconReply = (p: P) => (
  <Icon {...p}>
    <path d="M4 4.5h12a1.5 1.5 0 0 1 1.5 1.5v7a1.5 1.5 0 0 1-1.5 1.5H9l-3.5 3v-3H4A1.5 1.5 0 0 1 2.5 13V6A1.5 1.5 0 0 1 4 4.5Z" />
  </Icon>
);

export const IconEdit = (p: P) => (
  <Icon {...p}>
    <path d="M12.5 3.5l4 4L7 17H3v-4l9.5-9.5Z" />
  </Icon>
);

export const IconTrash = (p: P) => (
  <Icon {...p}>
    <path d="M3.5 5.5h13M8 5.5V3.5h4v2M5 5.5l.8 11h8.4l.8-11M8.5 8.5v5M11.5 8.5v5" />
  </Icon>
);

export const IconAttach = (p: P) => (
  <Icon {...p}>
    <path d="M16 9.5l-6.2 6.2a3.8 3.8 0 0 1-5.4-5.4l6.6-6.6a2.5 2.5 0 0 1 3.6 3.6l-6.5 6.5a1.2 1.2 0 0 1-1.8-1.8l5.9-5.9" />
  </Icon>
);

export const IconSend = (p: P) => (
  <Icon {...p}>
    <path d="M3 10l14-6.5L11 17l-2-5.5L3 10Z" />
    <path d="M9 11.5l8-8" />
  </Icon>
);

export const IconClose = (p: P) => (
  <Icon {...p}>
    <path d="M5 5l10 10M15 5L5 15" />
  </Icon>
);

export const IconPeople = (p: P) => (
  <Icon {...p}>
    <circle cx="7.5" cy="7" r="2.8" />
    <path d="M2.5 16.5c.5-3 2.5-4.5 5-4.5s4.5 1.5 5 4.5" />
    <path d="M13 4.5a2.6 2.6 0 0 1 0 5M14.5 12.3c1.6.5 2.7 1.9 3 4.2" />
  </Icon>
);

export const IconBolt = (p: P) => (
  <Icon {...p}>
    <path d="M11 2.5L4.5 11h5l-1 6.5L15.5 9h-5l.5-6.5Z" />
  </Icon>
);

export const IconSparkle = (p: P) => (
  <Icon {...p}>
    <path d="M10 2.5l1.6 4.4 4.4 1.6-4.4 1.6L10 14.5l-1.6-4.4L4 8.5l4.4-1.6L10 2.5Z" />
    <path d="M15.5 13.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4Z" />
  </Icon>
);

export const IconDown = (p: P) => (
  <Icon {...p}>
    <path d="M10 4v12M5 11l5 5 5-5" />
  </Icon>
);

export const IconChevron = (p: P) => (
  <Icon {...p}>
    <path d="M6 8l4 4 4-4" />
  </Icon>
);

export const IconCode = (p: P) => (
  <Icon {...p}>
    <path d="M7 6l-4 4 4 4M13 6l4 4-4 4" />
  </Icon>
);

export const IconCodeBlock = (p: P) => (
  <Icon {...p}>
    <rect x="2.5" y="3.5" width="15" height="13" rx="2" />
    <path d="M8 8l-2 2 2 2M12 8l2 2-2 2" />
  </Icon>
);

export const IconQuote = (p: P) => (
  <Icon {...p}>
    <path d="M4 4.5v11M8 7h8M8 10h8M8 13h5" />
  </Icon>
);

export const IconList = (p: P) => (
  <Icon {...p}>
    <path d="M8 5.5h9M8 10h9M8 14.5h9" />
    <circle cx="4" cy="5.5" r=".8" fill="currentColor" />
    <circle cx="4" cy="10" r=".8" fill="currentColor" />
    <circle cx="4" cy="14.5" r=".8" fill="currentColor" />
  </Icon>
);

export const IconReaction = (p: P) => (
  <Icon {...p}>
    <path d="M17 9.5a7 7 0 1 1-6.5-7" />
    <path d="M7 11.5c.7 1.2 1.8 2 3 2s2.3-.8 3-2M7.5 8h.01M12.5 8h.01M15.5 2.5v4M13.5 4.5h4" />
  </Icon>
);

export const IconBookmark = ({ size = 18, filled }: P & { filled?: boolean }) => (
  <svg viewBox="0 0 20 20" width={size} height={size} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden>
    <path d="M5.5 3h9a.5.5 0 0 1 .5.5V17l-5-3.5L5 17V3.5a.5.5 0 0 1 .5-.5Z" />
  </svg>
);

export const IconStar = ({ size = 18, filled }: P & { filled?: boolean }) => (
  <svg viewBox="0 0 20 20" width={size} height={size} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" aria-hidden>
    <path d="m10 2.8 2.2 4.6 5 .7-3.6 3.5.9 5-4.5-2.4-4.5 2.4.9-5L2.8 8.1l5-.7L10 2.8Z" />
  </svg>
);

export const IconClock = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="7" />
    <path d="M10 6v4l2.5 2.5" />
  </Icon>
);

export const IconGear = (p: P) => (
  <Icon {...p}>
    <path d="M16.25 8.63 L18.12 8.88 L18.12 11.12 L16.25 11.37 L15.39 13.45 L16.54 14.95 L14.95 16.54 L13.45 15.39 L11.37 16.25 L11.12 18.12 L8.88 18.12 L8.63 16.25 L6.55 15.39 L5.05 16.54 L3.46 14.95 L4.61 13.45 L3.75 11.37 L1.88 11.12 L1.88 8.88 L3.75 8.63 L4.61 6.55 L3.46 5.05 L5.05 3.46 L6.55 4.61 L8.63 3.75 L8.88 1.88 L11.12 1.88 L11.37 3.75 L13.45 4.61 L14.95 3.46 L16.54 5.05 L15.39 6.55 Z" />
    <circle cx="10" cy="10" r="2.6" />
  </Icon>
);

export const IconCompose = (p: P) => (
  <Icon {...p}>
    <path d="M9 3.5H5a1.5 1.5 0 0 0-1.5 1.5v10A1.5 1.5 0 0 0 5 16.5h10a1.5 1.5 0 0 0 1.5-1.5v-4" />
    <path d="m14.2 3.3 2.5 2.5L10 12.5l-3 .5.5-3 6.7-6.7Z" />
  </Icon>
);

export const IconBack = (p: P) => (
  <Icon {...p}>
    <path d="M16 10H4M9 5l-5 5 5 5" />
  </Icon>
);

export const IconForward = (p: P) => (
  <Icon {...p}>
    <path d="M4 10h12M11 5l5 5-5 5" />
  </Icon>
);

export const IconSearch = (p: P) => (
  <Icon {...p}>
    <circle cx="8.5" cy="8.5" r="5" />
    <path d="m12.3 12.3 4.2 4.2" />
  </Icon>
);

export const IconHelp = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="7.2" />
    <path d="M7.9 7.8a2.2 2.2 0 1 1 3 2c-.6.3-.9.8-.9 1.4v.3" />
    <circle cx="10" cy="14.2" r=".5" fill="currentColor" />
  </Icon>
);

export const IconShare = (p: P) => (
  <Icon {...p}>
    <path d="M11 4.5 16 9l-5 4.5" />
    <path d="M16 9H9.5A5.5 5.5 0 0 0 4 14.5V16" />
  </Icon>
);

export const IconLink = (p: P) => (
  <Icon {...p}>
    <path d="M8.5 11.5a3.5 3.5 0 0 0 5 0l2.5-2.5a3.5 3.5 0 0 0-5-5l-1 1M11.5 8.5a3.5 3.5 0 0 0-5 0L4 11a3.5 3.5 0 0 0 5 5l1-1" />
  </Icon>
);

export const IconMore = (p: P) => (
  <Icon {...p}>
    <circle cx="4.5" cy="10" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="10" cy="10" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="15.5" cy="10" r="1.2" fill="currentColor" stroke="none" />
  </Icon>
);

export const IconPin = (p: P) => (
  <Icon {...p}>
    <path d="M12.5 2.5l5 5-2.5 1-3 3 .5 3.5L11 16.5 3.5 9l1.5-1.5 3.5.5 3-3 1-2.5ZM7.5 12.5 3 17" />
  </Icon>
);

export const IconFile = (p: P) => (
  <Icon {...p}>
    <path d="M5 2.5h6.5L15.5 6.5V17a.5.5 0 0 1-.5.5H5a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5Z" />
    <path d="M11.5 2.5v4h4" />
  </Icon>
);

export const IconHeadphones = (p: P) => (
  <Icon {...p}>
    <path d="M3.5 13v-2.5a6.5 6.5 0 0 1 13 0V13" />
    <path d="M3.5 12.5h2.5v4.5H5a1.5 1.5 0 0 1-1.5-1.5v-3ZM16.5 12.5H14v4.5h1a1.5 1.5 0 0 0 1.5-1.5v-3Z" />
  </Icon>
);

export const IconMic = ({ off, ...p }: P & { off?: boolean }) => (
  <Icon {...p}>
    <rect x="7.5" y="2.5" width="5" height="9" rx="2.5" />
    <path d="M4.5 9.5a5.5 5.5 0 0 0 11 0M10 15v2.5" />
    {off && <path d="M3.5 3.5l13 13" />}
  </Icon>
);

export const IconVideo = ({ off, ...p }: P & { off?: boolean }) => (
  <Icon {...p}>
    <rect x="2.5" y="5.5" width="10.5" height="9" rx="1.5" />
    <path d="M13 9l4.5-2.5v7L13 11" />
    {off && <path d="M3.5 3.5l13 13" />}
  </Icon>
);

export const IconScreen = (p: P) => (
  <Icon {...p}>
    <rect x="2.5" y="3.5" width="15" height="10" rx="1.5" />
    <path d="M7 17h6M10 13.5V17M10 11V6.5M7.8 8.5 10 6.3l2.2 2.2" />
  </Icon>
);

export const IconLeave = (p: P) => (
  <Icon {...p}>
    <path d="M3 11.5c3.8-3.6 10.2-3.6 14 0l-1.8 2-2.7-1.2v-2c-2-.6-3-.6-5 0v2L4.8 13.5 3 11.5Z" />
  </Icon>
);

export const IconLayout = (p: P) => (
  <Icon {...p}>
    <rect x="3.5" y="3.5" width="5.5" height="5.5" rx="1" />
    <rect x="11" y="3.5" width="5.5" height="5.5" rx="1" />
    <rect x="3.5" y="11" width="5.5" height="5.5" rx="1" />
    <rect x="11" y="11" width="5.5" height="5.5" rx="1" />
  </Icon>
);

export const IconFullView = (p: P) => (
  <Icon {...p}>
    <path d="M3.5 7.5v-4h4M12.5 3.5h4v4M16.5 12.5v4h-4M7.5 16.5h-4v-4" />
  </Icon>
);

export const IconPopOut = (p: P) => (
  <Icon {...p}>
    <path d="M11 3.5h5.5V9M16.5 3.5 9.5 10.5" />
    <path d="M14 12v3.5a1 1 0 0 1-1 1H4.5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1H8" />
  </Icon>
);

export const IconExpand = (p: P) => (
  <Icon {...p}>
    <path d="M12 3.5h4.5V8M8 16.5H3.5V12M16.5 3.5 11.5 8.5M3.5 16.5l5-5" />
  </Icon>
);

export const IconCopy = (p: P) => (
  <Icon {...p}>
    <rect x="6.5" y="6.5" width="10" height="10" rx="1.5" />
    <path d="M13.5 6.5V4.5A1.5 1.5 0 0 0 12 3H4.5A1.5 1.5 0 0 0 3 4.5V12a1.5 1.5 0 0 0 1.5 1.5h2" />
  </Icon>
);

export const IconUnread = (p: P) => (
  <Icon {...p}>
    <path d="M16.5 9.5v5a1.5 1.5 0 0 1-1.5 1.5H5a1.5 1.5 0 0 1-1.5-1.5V6A1.5 1.5 0 0 1 5 4.5h6.5" />
    <circle cx="15.5" cy="4.5" r="2" />
  </Icon>
);

export const IconNumbered = (p: P) => (
  <Icon {...p}>
    <path d="M8.5 5.5h8.5M8.5 10h8.5M8.5 14.5h8.5M3.5 4.5l1-.5v3.5M3 13.2c.3-.5.8-.7 1.3-.7.6 0 1 .4 1 .9 0 .8-2.3 1.6-2.3 2.6h2.5" />
  </Icon>
);

export const IconAt = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="3" />
    <path d="M13 7v4a2 2 0 0 0 4 0v-1a7 7 0 1 0-2.8 5.6" />
  </Icon>
);

export const IconTask = (p: P) => (
  <Icon {...p}>
    <rect x="3" y="3" width="14" height="14" rx="3" />
    <path d="m6.8 10.2 2.2 2.2 4.4-4.6" />
  </Icon>
);

export const IconUp = (p: P) => (
  <Icon {...p}>
    <path d="M10 16V4M5 9l5-5 5 5" />
  </Icon>
);

export const IconPerson = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="6.5" r="3.2" />
    <path d="M3.5 17c.6-3.5 3-5.5 6.5-5.5s5.9 2 6.5 5.5" />
  </Icon>
);

export const IconPersonAdd = (p: P) => (
  <Icon {...p}>
    <circle cx="8" cy="6.5" r="3" />
    <path d="M2.5 16.5c.5-3.2 2.6-5 5.5-5s5 1.8 5.5 5M15.5 5.5v5M13 8h5" />
  </Icon>
);

export const IconSmile = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="7.2" />
    <path d="M7 11.5c.7 1.2 1.8 2 3 2s2.3-.8 3-2M7.6 8h.01M12.4 8h.01" />
  </Icon>
);

export const IconWorkflow = (p: P) => (
  <Icon {...p}>
    <rect x="2.5" y="3" width="5" height="5" rx="1.2" />
    <rect x="12.5" y="12" width="5" height="5" rx="1.2" />
    <path d="M7.5 5.5H13a2 2 0 0 1 2 2V12" />
  </Icon>
);

export const IconAgent = (p: P) => (
  <Icon {...p}>
    <rect x="3.5" y="6" width="13" height="10" rx="2.5" />
    <path d="M10 6V3.5M1.5 10v2.5M18.5 10v2.5M7.5 10.5h.01M12.5 10.5h.01M8 13.5h4" />
  </Icon>
);

export const IconServer = (p: P) => (
  <Icon {...p}>
    <rect x="3" y="3.5" width="14" height="5.5" rx="1.5" />
    <rect x="3" y="11" width="14" height="5.5" rx="1.5" />
    <path d="M6 6.25h.01M6 13.75h.01M9 6.25h5M9 13.75h5" />
  </Icon>
);

export const IconScales = (p: P) => (
  <Icon {...p}>
    <path d="M10 3v13.5M6.5 16.5h7M4 5.5h12" />
    <path d="M4 5.5 1.8 11M4 5.5 6.2 11M1.8 11a2.2 2.2 0 0 0 4.4 0ZM16 5.5 13.8 11M16 5.5l2.2 5.5M13.8 11a2.2 2.2 0 0 0 4.4 0Z" />
  </Icon>
);

export const IconPulse = (p: P) => (
  <Icon {...p}>
    <path d="M2 10.5h3.5l2-5 3.5 9 2-4h5" />
  </Icon>
);

export const IconDesktop = (p: P) => (
  <Icon {...p}>
    <rect x="2.5" y="3.5" width="15" height="10" rx="1.5" />
    <path d="M7 17h6M10 13.5V17M10 6v4.5M7.8 8.3 10 10.5l2.2-2.2" />
  </Icon>
);

export const IconCamera = (p: P) => (
  <Icon {...p}>
    <path d="M3.5 6.5h2.8l1.4-2h4.6l1.4 2h2.8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" />
    <circle cx="10" cy="11" r="3" />
  </Icon>
);

export const IconMoon = (p: P) => (
  <Icon {...p}>
    <path d="M16 12.5A6.5 6.5 0 0 1 7.5 4a6.5 6.5 0 1 0 8.5 8.5Z" />
  </Icon>
);

export const IconSun = (p: P) => (
  <Icon {...p}>
    <circle cx="10" cy="10" r="3.2" />
    <path d="M10 2.5V4M10 16v1.5M2.5 10H4M16 10h1.5M4.7 4.7l1 1M14.3 14.3l1 1M4.7 15.3l1-1M14.3 5.7l1-1" />
  </Icon>
);

export const IconBell = ({ off, ...p }: P & { off?: boolean }) => (
  <Icon {...p}>
    <path d="M5 13.5V9a5 5 0 0 1 10 0v4.5l1.5 2h-13l1.5-2ZM8.3 17.5a1.8 1.8 0 0 0 3.4 0" />
    {off && <path d="M3.5 3.5l13 13" />}
  </Icon>
);

export const IconSignOut = (p: P) => (
  <Icon {...p}>
    <path d="M8 3.5H4.5a1 1 0 0 0-1 1v11a1 1 0 0 0 1 1H8M13 6.5l3.5 3.5-3.5 3.5M16.5 10H8" />
  </Icon>
);

// IconFolder is a sidebar section; with mark, one to make or leave.
export const IconFolder = ({ mark, ...p }: P & { mark?: "plus" | "minus" }) => (
  <Icon {...p}>
    <path d="M2.5 5.5a1 1 0 0 1 1-1h4l1.5 2h7.5a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-10Z" />
    {mark && <path d={mark === "plus" ? "M10 9v5M7.5 11.5h5" : "M7.5 11.5h5"} />}
  </Icon>
);

export const IconCollapse = ({ open, ...p }: P & { open?: boolean }) => (
  <Icon {...p}>
    <path d={open ? "M6 7.5l4-4 4 4M6 12.5l4 4 4-4" : "M6 3.5l4 4 4-4M6 16.5l4-4 4 4"} />
  </Icon>
);

export const IconInbox = (p: P) => (
  <Icon {...p}>
    <path d="M2.5 11.5 5 4.5h10l2.5 7v4a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-4Z" />
    <path d="M2.5 11.5h4l1 2h5l1-2h4" />
  </Icon>
);

export const IconLock = (p: P) => (
  <Icon {...p}>
    <rect x="4" y="9" width="12" height="8.5" rx="1.5" />
    <path d="M6.75 9V6.5a3.25 3.25 0 0 1 6.5 0V9M10 12.5v2" />
  </Icon>
);

export const IconKey = (p: P) => (
  <Icon {...p}>
    <circle cx="6.5" cy="13.5" r="3.5" />
    <path d="m9 11 7.5-7.5M14 6l2 2M12 8l1.5 1.5" />
  </Icon>
);

export const IconShield = (p: P) => (
  <Icon {...p}>
    <path d="M10 2.5 16 5v4.5c0 3.8-2.5 6.6-6 8-3.5-1.4-6-4.2-6-8V5l6-2.5Z" />
  </Icon>
);

export const IconPalette = (p: P) => (
  <Icon {...p}>
    <path d="M10 2.5a7.5 7.5 0 0 0 0 15c1 0 1.5-.6 1.5-1.4 0-.9-.8-1.3-.8-2.1 0-.8.7-1.5 1.5-1.5h1.6a3.7 3.7 0 0 0 3.7-3.7c0-3.5-3.4-6.3-7.5-6.3Z" />
    <circle cx="6.5" cy="9" r=".8" />
    <circle cx="9" cy="6" r=".8" />
    <circle cx="12.5" cy="6.5" r=".8" />
  </Icon>
);

export const IconArchive = (p: P) => (
  <Icon {...p}>
    <rect x="2.5" y="3.5" width="15" height="4" rx="1" />
    <path d="M3.5 7.5v8a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-8M8 10.5h4" />
  </Icon>
);
